import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { ActionIcon, Alert, Badge, Box, Button, Center, Group, Loader, Stack, Tabs, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CreditCard, ExternalLink, FileText, LayoutDashboard } from 'lucide-react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { SurfaceCard } from '@/design/SurfaceCard';
import StatBand, { type StatItem } from '@/design/StatBand';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { BillingViewModel } from './BillingViewModel';
import { CardForm } from './CardForm';
import type { BillingInvoice } from './BillingService';

function money(minor: number, currency: string): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(minor / 100);
}

const STATUS_COLOR: Record<string, string> = { draft: 'gray', open: 'blue', paid: 'green', past_due: 'orange', void: 'red', uncollectible: 'red' };
const SUB_STATUS_TOKEN: Record<string, string> = { trialing: 'var(--bm-color-caution)', past_due: 'var(--bm-color-caution)', canceled: 'var(--bm-color-danger)', unpaid: 'var(--bm-color-danger)' };

export default function BillingPage(): ReactElement {
    const vm = useViewModel(BillingViewModel);
    const [editingCard, setEditingCard] = useState(false);
    const [cardSecret, setCardSecret] = useState<string | null>(null);

    useEffect(() => { void vm.load(); }, [vm]);

    const pk = vm.overview?.publishableKey ?? null;
    const stripePromise = useMemo<Promise<Stripe | null> | null>(() => (pk ? loadStripe(pk) : null), [pk]);

    const beginEditCard = async (): Promise<void> => {
        setEditingCard(true);
        setCardSecret(null);
        try {
            const { clientSecret } = await vm.createSetupIntent();
            setCardSecret(clientSecret);
        } catch (e) {
            notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed to start card setup' });
            setEditingCard(false);
        }
    };

    const closeEditCard = (): void => {
        setEditingCard(false);
        setCardSecret(null);
    };

    const invoiceColumns: MegaGridColumn<BillingInvoice>[] = [
        { key: 'number', header: 'Invoice', sortValue: (r) => r.number, render: (r) => <Text size="sm" fw={500}>{r.number}</Text> },
        { key: 'status', header: 'Status', sortValue: (r) => r.status, render: (r) => <Badge size="sm" variant="light" color={STATUS_COLOR[r.status] ?? 'gray'}>{r.status.replace('_', ' ')}</Badge> },
        { key: 'total', header: 'Amount', align: 'right', sortValue: (r) => r.totalMinor, render: (r) => <Text size="sm">{money(r.totalMinor, r.currency)}</Text> },
        { key: 'period', header: 'Period', sortValue: (r) => r.periodStart ?? '', render: (r) => <Text size="sm" c="dimmed">{r.periodStart?.slice(0, 10) ?? '—'}</Text> },
        { key: 'issued', header: 'Issued', sortValue: (r) => r.issuedAt ?? '', render: (r) => <Text size="sm" c="dimmed">{r.issuedAt?.slice(0, 10) ?? '—'}</Text> },
        {
            key: 'download', header: '', align: 'right', render: (r) => (
                <Tooltip label="Open PDF" withArrow>
                    <ActionIcon variant="subtle" color="gray" aria-label={`Open ${r.number}`} onClick={() => void openInvoice(r)}>
                        <ExternalLink size={16} />
                    </ActionIcon>
                </Tooltip>
            ),
        },
    ];

    const openInvoice = async (r: BillingInvoice): Promise<void> => {
        try {
            await vm.openInvoice(r.id);
        } catch (e) {
            notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed to open invoice' });
        }
    };

    const o = vm.overview;
    const sub = o?.subscription ?? null;
    const trialing = sub?.status === 'trialing';
    const stats: StatItem[] = sub
        ? [
            { label: 'Plan', value: sub.planName.toUpperCase() },
            { label: 'Price', value: `${money(sub.priceMinor, sub.currency)}/mo` },
            { label: 'Status', value: sub.status.replace('_', ' ').toUpperCase(), ...(SUB_STATUS_TOKEN[sub.status] ? { valueColor: SUB_STATUS_TOKEN[sub.status] } : {}) },
            { label: trialing ? 'Trial ends' : 'Next billing', value: sub.currentPeriodEnd?.slice(0, 10) ?? '—' },
        ]
        : [];

    return (
        <Box>
            <PageHeaderComponent title="Billing" subtitle="Your subscription, payment method, and invoices." />

            {vm.error && <Alert color="red" title="Failed to load" mb="md">{vm.error}</Alert>}

            {vm.loading && !o ? (
                <Center py="xl"><Loader size="sm" /></Center>
            ) : o?.provider === 'none' ? (
                <Alert color="blue" variant="light">Billing isn&apos;t set up by your provider yet.</Alert>
            ) : o ? (
                <Tabs defaultValue="overview" keepMounted={false}>
                    <Tabs.List>
                        <Tabs.Tab value="overview" leftSection={<LayoutDashboard size={15} />}>Overview</Tabs.Tab>
                        <Tabs.Tab value="invoices" leftSection={<FileText size={15} />}>Invoices</Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="overview" pt="xl">
                        <Stack gap="lg">
                            {trialing && !o.card && (
                                <Alert color="blue" variant="light" icon={<CreditCard size={16} />}>
                                    You&apos;re on a trial. Add a payment method to continue after it ends{sub?.currentPeriodEnd ? ` on ${sub.currentPeriodEnd.slice(0, 10)}` : ''}.
                                </Alert>
                            )}
                            {sub ? (
                                <StatBand stats={stats} />
                            ) : (
                                <SurfaceCard $padding="lg" $static>
                                    <Text size="sm" fw={600} mb={4}>Subscription</Text>
                                    <Text size="sm" c="dimmed">You&apos;re on a free or custom plan — no recurring charge.</Text>
                                </SurfaceCard>
                            )}

                            <SurfaceCard $padding="lg" $static>
                                <Group justify="space-between" mb="sm">
                                    <Text size="sm" fw={600}>Payment method</Text>
                                    {!editingCard && (
                                        <Button size="xs" variant="light" leftSection={<CreditCard size={14} />} onClick={() => void beginEditCard()}>
                                            {o.card ? 'Update card' : 'Add card'}
                                        </Button>
                                    )}
                                </Group>
                                {editingCard ? (
                                    stripePromise && cardSecret ? (
                                        <Elements stripe={stripePromise} options={{ clientSecret: cardSecret, appearance: { theme: 'stripe' } }}>
                                            <CardForm vm={vm} onDone={closeEditCard} onCancel={closeEditCard} />
                                        </Elements>
                                    ) : (
                                        <Center py="md"><Loader size="sm" /></Center>
                                    )
                                ) : o.card ? (
                                    <Group gap="sm">
                                        <Box style={{ display: 'inline-flex', padding: 8, borderRadius: 8, background: 'var(--bm-color-body)', border: '1px solid var(--bm-color-border)' }}>
                                            <CreditCard size={18} />
                                        </Box>
                                        <Box>
                                            <Text size="sm" fw={500} tt="capitalize">{o.card.brand ?? 'Card'} •••• {o.card.last4}</Text>
                                            <Text size="xs" c="dimmed">Expires {o.card.expMonth}/{o.card.expYear}</Text>
                                        </Box>
                                    </Group>
                                ) : (
                                    <Text size="sm" c="dimmed">No card on file. Add one to enable automatic billing.</Text>
                                )}
                            </SurfaceCard>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="invoices" pt="xl">
                        <MegaGrid
                            persistenceKey="tenant-invoices"
                            columns={invoiceColumns}
                            rows={o.invoices}
                            getRowKey={(r) => r.id}
                            defaultSort={{ key: 'issued', dir: 'desc' }}
                            searchAccessor={(r) => `${r.number} ${r.status}`}
                            searchPlaceholder="Search invoices"
                            emptyMessage="No invoices yet."
                            clientPageSize={15}
                        />
                    </Tabs.Panel>
                </Tabs>
            ) : null}
        </Box>
    );
}
